/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from "react";
import {
  Plus, Pencil, Trash2, ArrowUp, ArrowDown, Brain,
  HelpCircle, User, Dumbbell, BarChart2, ShieldQuestion,
  CheckSquare, Grid3X3, Target, Shield, Trophy, Gamepad2, Sparkles,
  Loader2,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  useQuizTypesAdmin,
  useCreateQuizType,
  useUpdateQuizType,
  useToggleQuizType,
  useDeleteQuizType,
  useReorderQuizTypes,
} from "@/hooks/api/useQuizTypes";
import {
  quizTypesService,
  type QuizType,
  type CreateQuizTypeRequest,
} from "@/api/services/quiz-types.service";
import { toast } from "react-toastify";
import { Upload, X } from "lucide-react";

// Supported icon keys (admin picks from this list)
const ICONS: Record<string, React.ElementType> = {
  HelpCircle, User, Dumbbell, BarChart2, ShieldQuestion, CheckSquare,
  Grid3X3, Brain, Target, Shield, Trophy, Gamepad2, Sparkles,
};

const COLORS = ["violet", "amber", "blue", "green", "red", "pink", "slate"] as const;

const emptyForm: CreateQuizTypeRequest = {
  slug: "",
  name: "",
  description: "",
  iconKey: "HelpCircle",
  iconUrl: null,
  colorKey: "violet",
  adminRoute: "",
  isActive: true,
};

export default function QuizTypesPage() {
  const { data, isLoading } = useQuizTypesAdmin();
  const { mutate: create, isPending: isCreating } = useCreateQuizType();
  const { mutate: update, isPending: isUpdating } = useUpdateQuizType();
  const { mutate: toggle } = useToggleQuizType();
  const { mutate: remove } = useDeleteQuizType();
  const { mutate: reorder } = useReorderQuizTypes();

  const types = useMemo(() => data?.types ?? [], [data]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<QuizType | null>(null);
  const [form, setForm] = useState<CreateQuizTypeRequest>(emptyForm);
  const [uploading, setUploading] = useState(false);

  const set = <K extends keyof CreateQuizTypeRequest>(k: K, v: CreateQuizTypeRequest[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2 MB"); return; }

    setUploading(true);
    try {
      const res = await quizTypesService.uploadIcon(file);
      set("iconUrl", res.url);
      toast.success("Icon uploaded");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (t: QuizType) => {
    setEditing(t);
    setForm({
      slug: t.slug,
      name: t.name,
      description: t.description ?? "",
      iconKey: t.iconKey ?? "HelpCircle",
      iconUrl: t.iconUrl ?? null,
      colorKey: t.colorKey ?? "violet",
      adminRoute: t.adminRoute,
      isActive: t.isActive,
    });
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditing(null); setForm(emptyForm); };

  const handleSave = () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!editing) {
      if (!form.slug.trim()) { toast.error("Slug is required for new types"); return; }
      if (!form.adminRoute.trim()) { toast.error("Admin route is required"); return; }
    }

    const payload: any = {
      name: form.name.trim(),
      description: form.description?.trim() || null,
      iconKey: form.iconKey,
      iconUrl: form.iconUrl ?? null,
      colorKey: form.colorKey,
      isActive: form.isActive,
    };

    if (editing) {
      if (form.adminRoute) payload.adminRoute = form.adminRoute.trim();
      update({ id: editing.id, data: payload }, {
        onSuccess: () => { toast.success("Updated"); closeDialog(); },
        onError: (e: any) => toast.error(e.response?.data?.message || "Update failed"),
      });
    } else {
      create(
        {
          ...payload,
          slug: form.slug.trim(),
          adminRoute: form.adminRoute.trim(),
        },
        {
          onSuccess: () => { toast.success("Created"); closeDialog(); },
          onError: (e: any) => toast.error(e.response?.data?.message || "Create failed"),
        },
      );
    }
  };

  const handleDelete = (t: QuizType) => {
    if (!confirm(`Delete "${t.name}"?\n\nThis will also remove it from the sidebar and mobile app.`)) return;
    remove(t.id, {
      onSuccess: () => toast.success("Deleted"),
      onError: (e: any) => toast.error(e.response?.data?.message || "Delete failed"),
    });
  };

  const move = (idx: number, dir: -1 | 1) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= types.length) return;
    const next = [...types];
    [next[idx], next[ni]] = [next[ni], next[idx]];
    reorder(next.map((t) => t.id), {
      onError: (e: any) => toast.error(e.response?.data?.message || "Reorder failed"),
    });
  };

  const activeCount = types.filter((t) => t.isActive).length;

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Quiz & Trivia</h1>
          <p className="text-gray-500 mt-1">
            Manage all quiz game types — order, rename, toggle visibility, and add new ones.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-violet-600 hover:bg-violet-700 text-white">
          <Plus className="mr-2 h-4 w-4" /> Add Game Type
        </Button>
      </div>

      <div className="flex gap-2 mb-4">
        <Badge variant="outline">Total: {types.length}</Badge>
        <Badge className="bg-green-100 text-green-700">Active: {activeCount}</Badge>
        <Badge className="bg-gray-100 text-gray-600">Inactive: {types.length - activeCount}</Badge>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Order</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Admin Route</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-gray-400">Loading…</TableCell>
              </TableRow>
            ) : types.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-gray-400">No quiz types</TableCell>
              </TableRow>
            ) : (
              types.map((t, i) => {
                const Icon = (t.iconKey && ICONS[t.iconKey]) || HelpCircle;
                return (
                  <TableRow key={t.id} className={!t.isActive ? "opacity-50" : ""}>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500 font-mono w-6 text-center">{t.order}</span>
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => move(i, -1)}
                            disabled={i === 0}
                            className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                            title="Move up"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => move(i, 1)}
                            disabled={i === types.length - 1}
                            className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                            title="Move down"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {t.iconUrl ? (
                          <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center overflow-hidden" style={{ backgroundColor: "#0D8C3C" }}>
                            <img src={t.iconUrl} alt="" className="w-7 h-7 object-contain" />
                          </div>
                        ) : (
                          <div className={`w-9 h-9 rounded-lg bg-${t.colorKey ?? "violet"}-100 flex items-center justify-center`}>
                            <Icon className={`h-5 w-5 text-${t.colorKey ?? "violet"}-600`} />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{t.name}</p>
                          {t.description && (
                            <p className="text-xs text-gray-400 line-clamp-1">{t.description}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-700">{t.slug}</code>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs text-gray-500">{t.adminRoute}</code>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={t.isActive}
                        onCheckedChange={() => toggle(t.id)}
                        className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-500"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(t)}>
                          <Pencil className="h-4 w-4 text-gray-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(t)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>
              {editing ? `Edit: ${editing.name}` : "Add New Game Type"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block text-sm">Display Name <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Who Am I" />
            </div>

            <div>
              <Label className="mb-1 block text-sm">Description</Label>
              <Textarea
                value={form.description ?? ""}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Short subtitle for mobile listing"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-sm">
                  Slug {!editing && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                  placeholder="who-am-i"
                  disabled={!!editing}
                />
                {editing && <p className="text-xs text-gray-400 mt-1">Slug is immutable once created</p>}
              </div>
              <div>
                <Label className="mb-1 block text-sm">Admin Route <span className="text-red-500">*</span></Label>
                <Input
                  value={form.adminRoute}
                  onChange={(e) => set("adminRoute", e.target.value)}
                  placeholder="/quiz/who-am-i"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-sm">Icon</Label>
                <select
                  value={form.iconKey ?? "HelpCircle"}
                  onChange={(e) => set("iconKey", e.target.value)}
                  className="w-full border rounded-md px-2 py-2 text-sm bg-white"
                >
                  {Object.keys(ICONS).map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="mb-1 block text-sm">Color</Label>
                <select
                  value={form.colorKey ?? "violet"}
                  onChange={(e) => set("colorKey", e.target.value)}
                  className="w-full border rounded-md px-2 py-2 text-sm bg-white"
                >
                  {COLORS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Mobile-app icon (overrides the local fallback image) */}
            <div>
              <Label className="mb-1 block text-sm">
                Mobile icon <span className="text-xs text-slate-400 font-normal">(optional — shown on the app's quiz list)</span>
              </Label>
              {form.iconUrl ? (
                <div className="flex items-center gap-3">
                  <div className="relative h-16 w-16 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                    <img
                      src={form.iconUrl}
                      alt=""
                      className={`max-h-full max-w-full object-contain transition-opacity ${uploading ? "opacity-30" : ""}`}
                    />
                    {uploading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                        <Loader2 className="h-5 w-5 text-violet-600 animate-spin" />
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => set("iconUrl", null)}
                    disabled={uploading}
                    className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Remove
                  </Button>
                  <label
                    className={`text-xs cursor-pointer hover:underline ${uploading ? "text-violet-600 pointer-events-none" : "text-slate-500"}`}
                  >
                    {uploading ? "Replacing…" : "Replace"}
                    <input type="file" accept="image/*" hidden onChange={handleIconUpload} disabled={uploading} />
                  </label>
                </div>
              ) : (
                <div className="space-y-2">
                  <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md text-sm text-slate-600 hover:bg-slate-50 cursor-pointer w-fit">
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {uploading ? "Uploading…" : "Upload icon (PNG, under 2 MB)"}
                    <input type="file" accept="image/*" hidden onChange={handleIconUpload} disabled={uploading} />
                  </label>
                  <Input
                    type="url"
                    placeholder="Or paste image URL"
                    className="text-sm"
                    value={form.iconUrl ?? ""}
                    onChange={(e) => set("iconUrl", e.target.value || null)}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={form.isActive ?? true}
                onCheckedChange={(v) => set("isActive", v)}
                className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-500"
              />
              <Label className="text-sm">Active (visible in sidebar & mobile app)</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {isCreating || isUpdating ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
